/**
 * What one event uses: the workflow that runs the event, and the workflows its
 * fixtures go through, with choose and clear.
 *
 * A fixture at an event uses, in this order: the choice for its sport, the
 * choice for every sport, then its sport's own scoring. The event itself has no
 * fallback: without a choice the operations console cannot open. Both rules are
 * in `omnium_core/workflows/identity.py` (`fixture_workflow`, `event_workflow`).
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, TriangleAlert } from "lucide-react";

import {
  Button,
  Card,
  Empty,
  Field,
  FieldRow,
  Pill,
  Problem,
  Section,
  Select,
  Stack,
  Table,
  Td,
  Th,
} from "@/components/kit";
import styles from "@/components/workflows/workflows.module.css";
import {
  chooseEventWorkflow,
  choiceTarget,
  getEventWorkflows,
  offeredFor,
  parseTarget,
  targetKey,
  targetLabel,
  undoEventWorkflow,
  type ChoiceTarget,
  type EventChoice,
  type EventWorkflows as EventWorkflowsData,
} from "@/lib/api/workflows";
import { displayName } from "@/lib/format";
import { errorText, useApi } from "@/spa/useApi";

type Part = {
  target: ChoiceTarget;
  label: string;
  choice: EventChoice | undefined;
  /** What the row means once something is chosen. */
  meaning: string;
  /** What it uses, and what that means, when nothing is chosen. */
  without: { uses: string; meaning: string; warn: boolean };
};

export function EventWorkflows({ code, sports }: { code: string; sports: string[] }) {
  const loaded = useApi(() => getEventWorkflows(code), [code]);
  const [fresh, setFresh] = useState<{ code: string; data: EventWorkflowsData } | null>(null);
  const data = fresh?.code === code ? fresh.data : loaded.data;

  const [targetText, setTargetText] = useState("fixture:");
  const [workflow, setWorkflow] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const run = async (label: string, work: () => Promise<EventWorkflowsData>) => {
    setBusy(label);
    setProblem(null);
    try {
      setFresh({ code, data: await work() });
    } catch (thrown) {
      setProblem(errorText(thrown, "omnium did not save that"));
    } finally {
      setBusy(null);
    }
  };

  if (loaded.error && !data) {
    return (
      <Section title="Workflows">
        <Problem>Could not load this event&apos;s workflows: {loaded.error}</Problem>
      </Section>
    );
  }

  const choices = data?.choices ?? [];
  const find = (target: ChoiceTarget) =>
    choices.find((choice) => targetKey(choiceTarget(choice)) === targetKey(target));

  const plain: Omit<Part, "choice">[] = [
    {
      target: { subject: "event", sport: null },
      label: "The event itself",
      meaning: "Runs the event: medal table, players and schedule. The operations console uses it.",
      without: {
        uses: "Nothing chosen",
        meaning: "There is no default. The operations console cannot open until you choose one.",
        warn: true,
      },
    },
    {
      target: { subject: "fixture", sport: null },
      label: "Fixtures in every sport",
      meaning: "Every sport's fixtures use it, unless a sport has its own choice below.",
      without: {
        uses: "Each sport's own scoring (the default)",
        meaning:
          "A sport without its own scoring, such as archery, has no workflow at this event until you choose one.",
        warn: false,
      },
    },
    ...choices
      .filter((choice) => choice.subject !== "event" && choice.sportCode)
      .map((choice) => ({
        target: choiceTarget(choice),
        label: targetLabel(choice),
        meaning: "Chosen for this sport only. It comes before the choice for every sport.",
        without: { uses: "", meaning: "", warn: false },
      })),
  ];
  const parts: Part[] = plain.map((part) => ({ ...part, choice: find(part.target) }));

  const target = parseTarget(targetText);
  const fits = data ? offeredFor(data.offered, target) : [];
  const picked = fits.some((row) => row.code === workflow) ? workflow : (fits[0]?.code ?? "");
  const pickedRow = fits.find((row) => row.code === picked);
  const current = find(target);
  const same = current?.workflow === picked;
  const instead = current ? ` instead of ${current.workflowName}` : "";
  const sentence = !pickedRow
    ? null
    : same
      ? `${targetLabel(target)} already ${target.subject === "event" ? "uses" : "use"} ${pickedRow.name}.`
      : target.subject === "event"
        ? `The event will be run with ${pickedRow.name}${instead}. The operations console will use it.`
        : `${targetLabel(target)} will use ${pickedRow.name}${instead}, at this event only. A fixture that already has scores stays on the workflow it started with.`;

  return (
    <Section
      title="Workflows"
      note="What this event uses. A choice here is for this event only; other events do not change."
    >
      {!data ? (
        <Card>
          <Empty title="Loading…" />
        </Card>
      ) : (
        <Stack>
          <ul className={styles.how}>
            <li>
              A fixture uses, in this order: the choice for its sport, then the choice for every
              sport, then its sport&apos;s own scoring.
            </li>
            <li>Clear a choice to go back to that order.</li>
          </ul>

          <Table>
            <thead>
              <tr>
                <Th>Part of the event</Th>
                <Th>Uses</Th>
                <Th>What that means</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {parts.map((part) => {
                const key = targetKey(part.target);
                return (
                  <tr key={key}>
                    <Td strong>{part.label}</Td>
                    <Td>
                      {part.choice ? (
                        <div className={styles.appName}>
                          <Link to={`/workflows/${encodeURIComponent(part.choice.workflow)}`}>
                            {part.choice.workflowName}
                          </Link>
                          <Pill tone="accent">
                            <Check size={12} aria-hidden className={styles.pillIcon} />
                            Chosen for this event
                          </Pill>
                        </div>
                      ) : part.without.warn ? (
                        <span className={styles.warnText}>
                          <TriangleAlert size={14} aria-hidden />
                          {part.without.uses}
                        </span>
                      ) : (
                        part.without.uses
                      )}
                    </Td>
                    <Td quiet style={{ whiteSpace: "normal", minWidth: "16rem" }}>
                      {part.choice ? part.meaning : part.without.meaning}
                    </Td>
                    <Td>
                      {part.choice && (
                        <Button
                          tone="subtle"
                          disabled={busy !== null}
                          onClick={() =>
                            void run(key, () => undoEventWorkflow(code, part.target))
                          }
                        >
                          {busy === key ? "Clearing…" : "Clear choice"}
                        </Button>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          <Card>
            <Stack>
              <h3 className={styles.formTitle}>Choose what a part of the event uses</h3>
              <FieldRow>
                <Field
                  label="For"
                  htmlFor="event-workflow-for"
                  hint="The event itself, or the fixtures of every sport or of one sport."
                >
                  <Select
                    id="event-workflow-for"
                    value={targetText}
                    onChange={(event) => setTargetText(event.target.value)}
                  >
                    <option value="event">The event itself</option>
                    <option value="fixture:">Fixtures in every sport</option>
                    {sports.map((sport) => (
                      <option key={sport} value={`fixture:${sport}`}>
                        {displayName(sport)} fixtures
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field
                  label="Use"
                  htmlFor="event-workflow-use"
                  hint="Only the workflows that fit are listed."
                >
                  <Select
                    id="event-workflow-use"
                    value={picked}
                    disabled={fits.length === 0}
                    onChange={(event) => setWorkflow(event.target.value)}
                  >
                    {fits.length === 0 && <option value="">No workflow fits that</option>}
                    {fits.map((row) => (
                      <option key={row.code} value={row.code}>
                        {row.name}
                        {row.version === null ? " (not published)" : ` · v${row.version}`}
                      </option>
                    ))}
                  </Select>
                </Field>
              </FieldRow>
              {sentence && <p className={styles.preview}>{sentence}</p>}
              <div>
                <Button
                  tone="primary"
                  disabled={!picked || busy !== null || same}
                  onClick={() =>
                    void run("choose", () =>
                      chooseEventWorkflow(code, {
                        subject: target.subject,
                        sport: target.sport,
                        workflow: picked,
                      }),
                    )
                  }
                >
                  {busy === "choose" ? "Choosing…" : "Choose"}
                </Button>
              </div>
              {problem && (
                <p role="alert" className={styles.alert}>
                  {problem}
                </p>
              )}
            </Stack>
          </Card>
        </Stack>
      )}
    </Section>
  );
}
